import { useState, useEffect } from 'react';

const PostFormStep1 = ({ location, initialData, onNext, onClose, stepBar, onIsSuperChange, isCollapsed = false }) => {
  const [photoFile, setPhotoFile] = useState(initialData?.photoFile ?? null);
  const [photoPreview, setPhotoPreview] = useState(
    initialData?.photoFile ? URL.createObjectURL(initialData.photoFile) : null
  );
  const [nickname, setNickname] = useState(initialData?.nickname ?? '');
  const [isSuper, setIsSuper] = useState(initialData?.isSuper ?? false);
  const [errors, setErrors] = useState({});
  const [address, setAddress] = useState(initialData?.address ?? null);

  // Geocoding で住所取得
  useEffect(() => {
    if (!location || initialData?.address) return;
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location, language: 'ja' }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setAddress(results[0].formatted_address);
      }
    });
  }, [location, initialData?.address]);

  const isAllowedImage = (file) => {
    if (!file) return false;
    const allowed = ['image/png', 'image/jpeg'];
    return allowed.includes(file.type);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAllowedImage(file)) {
      setErrors((s) => ({ ...s, photo: '対応している画像形式は PNG / JPG のみです' }));
      setPhotoFile(null);
      setPhotoPreview(null);
      return;
    }
    setErrors((s) => ({ ...s, photo: undefined }));
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const validate = () => {
    const errs = {};
    if (!photoFile) errs.photo = '写真を選択してください';
    // if (!nickname.trim()) errs.nickname = 'ニックネームを入力してください';
    if (nickname.length > 20) errs.nickname = 'ニックネームは20文字以内にしてください';
    return errs;
  };

  const handleNext = () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    onNext({ photoFile, nickname: nickname.trim(), isSuper, address });
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-lp rounded-t-2xl bg-white shadow-xl overflow-y-auto" style={{ maxHeight: '85dvh' }}>
      {stepBar}

      <div className={`p-4 space-y-6 ${isCollapsed ? 'hidden' : ''}`}>
        {/* 足あと投稿の説明 */}
        <div className="rounded-xl bg-amber-50 p-4 border border-amber-200">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-bold text-amber-900 mb-2">足あとフォトとは</h3>
              <p className="text-xs text-amber-800 leading-relaxed">
                48時間限定で表示される思い出のフォトを共有できる機能です。出会った動物の写真やARフォト、お出かけ中に撮った写真など、あなたの大切な思い出を投稿してください。
                <br/>
                「この写真、絶対ズー友のみんなに見てほしい！」そんな時は、すっごーい！足あとボタンを押すと、表示時間が72時間に伸びます。
              </p>
            </div>
          </div>
        </div>

        {/* 投稿位置 */}
        {location && (
          <div className="space-y-1.5">
            <label className="block text-sm font-semibold text-gray-700">投稿場所</label>
            <div className="rounded-lg bg-gray-50 px-3 py-2.5">
              <p className="text-sm text-gray-700">
                {address ?? `${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`}
              </p>
              {!address && <p className="text-xs text-gray-400 mt-1">取得中...</p>}
            </div>
          </div>
        )}

        {/* 写真 */}
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-gray-700">写真 <span className="text-red-500 text-xs font-normal ml-1">必須</span></label>
          <label className="block cursor-pointer rounded-xl border-2 border-dashed border-gray-300 p-4 text-center hover:border-amber-400 transition-colors">
            {photoPreview ? (
              <img src={photoPreview} alt="preview" className="mx-auto max-h-40 rounded-lg object-cover" />
            ) : (
              <span className="text-sm text-gray-400">写真を選択してください</span>
            )}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={handlePhotoChange} aria-label="写真を選択" />
          </label>
          {errors.photo && <p className="text-xs text-red-500">{errors.photo}</p>}
        </div>

        {/* ニックネーム */}
        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-gray-700">ニックネーム <span className="text-gray-400 text-xs font-normal ml-1">(任意)</span></label>
          <div>
            <input
              type="text"
              placeholder="20文字以内"
              maxLength={20}
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="ニックネーム"
            />
            <div className="mt-1 text-right text-xs text-gray-400">{nickname.length} / 20</div>
          </div>
          {errors.nickname && <p className="text-xs text-red-500">{errors.nickname}</p>}
        </div>

        {/* すっごーい！足あと */}
        <label className="flex cursor-pointer items-center gap-3 rounded-xl bg-green-50 px-3.5 py-3">
          <input
            type="checkbox"
            checked={isSuper}
            onChange={(e) => {
              setIsSuper(e.target.checked);
              if (onIsSuperChange) onIsSuperChange(e.target.checked);
            }}
            className="w-5 h-5 cursor-pointer flex-shrink-0"
            aria-label="すっごーい！足あとにする"
          />
          <div className="flex-1">
            <p className="text-sm font-semibold text-green-700">すっごーい！足あと を有効にする</p>
            {/* <p className="text-xs text-green-600 mt-0.5">表示時間が72時間に伸びます。</p> */}
          </div>
        </label>

        {/* 次へボタン */}
        <button
          onClick={handleNext}
          className="w-full rounded-full bg-amber-500 hover:bg-amber-600 py-3 font-bold text-white transition-colors mt-2"
        >
          次へ
        </button>
      </div>
    </div>
  );
};

export default PostFormStep1;